import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  auditTime,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  startWith,
  Subscription,
} from 'rxjs';
import { AiTutorModeService } from 'src/app/services/ai-tutor-mode.service';
import { LunaPresenceService } from 'src/app/services/luna-presence.service';
import {
  LunaElsewhere,
  lunaElsewhereFor,
  lunaTabIdFor,
} from 'src/app/services/luna-workspace/luna-location.util';
import { LunaWorkspaceState } from 'src/app/services/luna-workspace/luna-workspace-state';
import { isWorkspaceApp } from 'src/app/services/luna-workspace/luna-workspace-tabs.util';
import { AppIcon, ItemsCanvasService } from 'src/app/services/items-canvas.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { ItemModel } from 'src/app/models/itemModel';
import {
  AppConfigurationService,
  IAppInformation,
} from 'src/app/sessions/session/iframe/additional-apps.utils';
import { isBoardItem } from 'src/app/sessions/common/y-session';
import type { YCanvas } from 'src/app/sessions/session/wb-canvas/fabric-utils';

/**
 * Luna workspace mode in the app: {@link LunaWorkspaceState} wired to the URL flag, the session
 * view and the whiteboard canvas, whose app items become tabs.
 */
@Injectable({ providedIn: 'root' })
export class LunaWorkspaceService extends LunaWorkspaceState implements OnDestroy {
  private canvasSubscription?: Subscription;

  /** The tab Luna is working in (an app's uid or 'whiteboard'), or null before she has been anywhere. */
  readonly lunaTabId$: Observable<string | null>;

  /** The Luna panel's "Luna is on the …" card, while she is in a tab the student isn't on. */
  readonly lunaElsewhere$: Observable<LunaElsewhere | null>;

  constructor(
    private readonly aiTutorMode: AiTutorModeService,
    sharedDataService: SessionSharedDataService,
    private readonly zone: NgZone,
    private readonly appConfigurationService: AppConfigurationService,
    private readonly itemsCanvasService: ItemsCanvasService,
    lunaPresence: LunaPresenceService,
  ) {
    super(aiTutorMode.workspaceEnabled$, sharedDataService.sessionView);
    // What Luna is making: tabs that fill up while an app is built, and the "ready" toast.
    this.subscriptions.add(
      lunaPresence.creations$.subscribe((creations) => this.setCreations(creations)),
    );
    this.lunaTabId$ = combineLatest([lunaPresence.lunaLocation$, this.tabs$]).pipe(
      map(([location, tabs]) => lunaTabIdFor(location, tabs)),
      distinctUntilChanged(),
    );
    // Her latest whiteboard section title, for the card's second line.
    const sectionTitle$ = lunaPresence.lunaFocus$.pipe(
      filter((focus) => focus?.reason === 'section-start' && !!focus.title),
      map((focus) => focus?.title ?? null),
      startWith(null as string | null),
    );
    this.lunaElsewhere$ = combineLatest([
      this.enabled$,
      this.lunaTabId$,
      this.activeTabIds$,
      this.tabs$,
      sectionTitle$,
    ]).pipe(
      map(([enabled, lunaTabId, activeTabIds, tabs, title]) =>
        enabled ? lunaElsewhereFor(lunaTabId, activeTabIds, tabs, title) : null,
      ),
    );
  }

  get enabled(): boolean {
    return this.aiTutorMode.workspaceEnabled;
  }

  attachCanvas(canvas: YCanvas): void {
    this.detachCanvas();
    // The canvas fires its events outside Angular; re-enter so the tab bar updates.
    const refresh = () =>
      this.zone.run(() =>
        this.setBoardItems(
          canvas
            .getObjects()
            .filter(isBoardItem)
            .map((item) => ({
              itemType: item.itemType,
              configurations: item.configurations,
              left: item.left,
              icon: this.iconFor(item.itemType, item.configurations),
              title: this.catalogApp(item.configurations)?.title ?? null,
            })),
        ),
      );
    // A move made elsewhere (Luna moving an app to its own tab) fires none of these, so also
    // look at where the apps are after renders, and refresh only when one actually moved.
    const appPlaces = () =>
      canvas
        .getObjects()
        .filter(isBoardItem)
        .filter((item) => isWorkspaceApp(item.itemType))
        .map((item) => `${item.configurations.uid}@${Math.round(item.left ?? 0)}`)
        .join('|');
    const appMoved$ = fromEvent(canvas, 'after:render').pipe(
      auditTime(500),
      map(appPlaces),
      distinctUntilChanged(),
    );
    this.canvasSubscription = merge(
      fromEvent(canvas, 'object:added'),
      fromEvent(canvas, 'object:removed'),
      fromEvent(canvas, 'object:modified'),
      appMoved$,
    )
      .pipe(debounceTime(200))
      .subscribe(refresh);
    refresh();
  }

  /** The app's record in the app catalog (AppInformation), as the board's app header finds it. */
  private catalogApp(configurations: unknown): IAppInformation | undefined {
    const appInstanceId = (configurations as { appInstanceId?: string }).appInstanceId;
    return appInstanceId ? this.appConfigurationService.getAppByUid(appInstanceId) : undefined;
  }

  /** The icon the app catalog gives this app, as its board header shows it. */
  private iconFor(itemType: ItemModel, configurations: unknown): AppIcon | null {
    const catalogIcon = this.catalogApp(configurations)?.icon;
    // Mario apps fall back to the code icon, the same default the board header uses.
    const icon = catalogIcon || (itemType === ItemModel.Mario ? 'code_iframe' : '');
    if (!icon) {
      return null;
    }
    return {
      type: this.itemsCanvasService.getIconSource(icon),
      content: icon,
      height: 18,
      width: 18,
    };
  }

  detachCanvas(): void {
    this.canvasSubscription?.unsubscribe();
    this.canvasSubscription = undefined;
    this.clearBoard();
  }

  ngOnDestroy(): void {
    this.detachCanvas();
    this.dispose();
  }
}
